import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  CalendarBlank, CaretLeft, CaretRight, GoogleLogo, Plus, PencilSimple, Trash, LinkSimpleBreak, ArrowClockwise,
} from "@phosphor-icons/react";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, format,
  isSameMonth, isSameDay, addMonths, subMonths, addWeeks, subWeeks,
  startOfDay, endOfDay, addHours, parseISO, isWithinInterval,
} from "date-fns";
import { it } from "date-fns/locale";

const VIEWS = [
  { key: "month", label: "Mese" },
  { key: "week", label: "Settimana" },
  { key: "day", label: "Giorno" },
];

function localInputValue(dateIso) {
  // Convert ISO to "YYYY-MM-DDTHH:mm" for datetime-local input
  const d = dateIso ? new Date(dateIso) : new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function inputToIso(local) {
  // datetime-local returns YYYY-MM-DDTHH:mm (no seconds, no tz)
  if (!local) return null;
  const d = new Date(local);
  return d.toISOString();
}

function eventStartsInInterval(ev, start, end) {
  if (!ev.start) return false;
  try {
    const s = ev.all_day ? startOfDay(parseISO(ev.start)) : parseISO(ev.start);
    return isWithinInterval(s, { start, end });
  } catch {
    return false;
  }
}

export default function CalendarPage() {
  useDocumentTitle("Calendario");
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState({ loading: true, configured: false, connected: false, google_email: null });
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [dialog, setDialog] = useState({ open: false, editing: null }); // editing: event or null (create)
  const [selectedDay, setSelectedDay] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  // Handle callback ?gcal_connected=1 or ?gcal_error=
  useEffect(() => {
    if (searchParams.get("gcal_connected")) {
      toast.success("Google Calendar collegato!");
      searchParams.delete("gcal_connected");
      setSearchParams(searchParams, { replace: true });
      setRefreshTick((t) => t + 1);
    }
    const err = searchParams.get("gcal_error");
    if (err) {
      toast.error(`Errore collegamento Google: ${err}`);
      searchParams.delete("gcal_error");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadStatus = useCallback(async () => {
    setStatus((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get("/calendar/status");
      setStatus({ loading: false, ...data });
    } catch (e) {
      setStatus({ loading: false, configured: false, connected: false });
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus, refreshTick]);

  const range = useMemo(() => {
    if (view === "month") {
      const monthStart = startOfMonth(cursor);
      const monthEnd = endOfMonth(cursor);
      return {
        gridStart: startOfWeek(monthStart, { weekStartsOn: 1 }),
        gridEnd: endOfWeek(monthEnd, { weekStartsOn: 1 }),
      };
    }
    if (view === "week") {
      return {
        gridStart: startOfWeek(cursor, { weekStartsOn: 1 }),
        gridEnd: endOfWeek(cursor, { weekStartsOn: 1 }),
      };
    }
    return { gridStart: startOfDay(cursor), gridEnd: endOfDay(cursor) };
  }, [view, cursor]);

  const loadEvents = useCallback(async () => {
    if (!status.connected) return;
    setLoadingEvents(true);
    try {
      // Widen fetch range a bit for smoothness
      const from = startOfMonth(subMonths(cursor, 1)).toISOString();
      const to = endOfMonth(addMonths(cursor, 2)).toISOString();
      const { data } = await api.get("/calendar/events", { params: { time_min: from, time_max: to } });
      setEvents(data.events || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoadingEvents(false);
    }
  }, [status.connected, cursor]);

  useEffect(() => { loadEvents(); }, [loadEvents, refreshTick]);

  const connect = async () => {
    try {
      const { data } = await api.get("/calendar/authorize");
      if (data?.authorization_url) {
        window.location.href = data.authorization_url;
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Scollegare Google Calendar? Non vedrai più i tuoi eventi qui.")) return;
    try {
      await api.post("/calendar/disconnect");
      toast.success("Google Calendar scollegato");
      setEvents([]);
      setRefreshTick((t) => t + 1);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const openCreate = (day) => {
    setSelectedDay(day || cursor);
    setDialog({ open: true, editing: null });
  };

  const openEdit = (ev) => setDialog({ open: true, editing: ev });

  const removeEvent = async (ev) => {
    if (!window.confirm(`Eliminare l'evento "${ev.summary}" da Google Calendar?`)) return;
    try {
      await api.delete(`/calendar/events/${ev.id}`);
      toast.success("Evento eliminato");
      loadEvents();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const navigate = (dir) => {
    if (view === "month") setCursor(dir === "next" ? addMonths(cursor, 1) : subMonths(cursor, 1));
    else if (view === "week") setCursor(dir === "next" ? addWeeks(cursor, 1) : subWeeks(cursor, 1));
    else setCursor(dir === "next" ? addDays(cursor, 1) : addDays(cursor, -1));
  };

  const label = useMemo(() => {
    if (view === "month") return format(cursor, "MMMM yyyy", { locale: it });
    if (view === "week") {
      const s = startOfWeek(cursor, { weekStartsOn: 1 });
      const e = endOfWeek(cursor, { weekStartsOn: 1 });
      return `${format(s, "d MMM", { locale: it })} – ${format(e, "d MMM yyyy", { locale: it })}`;
    }
    return format(cursor, "EEEE d MMMM yyyy", { locale: it });
  }, [view, cursor]);

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="calendar-page">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">// AGENDA</div>
          <h1 className="text-4xl font-display font-bold tracking-tight">Calendario</h1>
          <p className="text-muted-foreground mt-2">Appuntamenti sincronizzati con il tuo Google Calendar.</p>
        </div>
        <div className="flex items-center gap-2">
          {status.connected && (
            <>
              <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/15 font-mono text-[10px] uppercase" data-testid="gcal-connected-badge">
                <GoogleLogo size={12} weight="bold" className="mr-1" /> Collegato
              </Badge>
              <Button variant="outline" size="sm" onClick={() => setRefreshTick((t) => t + 1)} className="gap-1 rounded-full" data-testid="calendar-refresh-btn">
                <ArrowClockwise size={14} /> Aggiorna
              </Button>
              <Button onClick={() => openCreate(new Date())} className="gap-1 rounded-full" data-testid="calendar-new-event-btn">
                <Plus size={16} weight="bold" /> Nuovo appuntamento
              </Button>
            </>
          )}
        </div>
      </header>

      {!status.loading && !status.configured && (
        <Card className="p-6 border-border/70 rounded-lg">
          <div className="text-sm text-muted-foreground">
            Google Calendar non è configurato sul server. Contatta l'amministratore per impostare le credenziali OAuth.
          </div>
        </Card>
      )}

      {!status.loading && status.configured && !status.connected && (
        <Card className="p-8 border-border/70 rounded-lg text-center max-w-lg mx-auto">
          <div className="mb-4 h-14 w-14 mx-auto rounded-full bg-primary/10 text-primary grid place-items-center">
            <GoogleLogo size={28} weight="duotone" />
          </div>
          <h2 className="font-display font-bold text-xl mb-2">Collega il tuo Google Calendar</h2>
          <p className="text-sm text-muted-foreground mb-5">
            Autorizza l'accesso per visualizzare, creare e modificare gli appuntamenti direttamente dal portale Diotech.
            I dati restano sul tuo account Google.
          </p>
          <Button onClick={connect} className="rounded-full gap-2" data-testid="gcal-connect-btn">
            <GoogleLogo size={16} weight="bold" /> Collega Google Calendar
          </Button>
        </Card>
      )}

      {status.connected && (
        <>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setCursor(new Date())} className="rounded-full" data-testid="cal-today-btn">Oggi</Button>
              <div className="flex">
                <Button variant="ghost" size="icon" onClick={() => navigate("prev")} className="rounded-full" data-testid="cal-prev-btn"><CaretLeft size={16} /></Button>
                <Button variant="ghost" size="icon" onClick={() => navigate("next")} className="rounded-full" data-testid="cal-next-btn"><CaretRight size={16} /></Button>
              </div>
              <div className="font-display font-bold text-lg capitalize ml-2" data-testid="cal-label">{label}</div>
              {loadingEvents && <div className="text-xs text-muted-foreground ml-2 font-mono">caricamento…</div>}
            </div>
            <div className="flex items-center gap-1 bg-secondary/60 rounded-full p-1">
              {VIEWS.map((v) => (
                <button
                  key={v.key}
                  onClick={() => setView(v.key)}
                  data-testid={`cal-view-${v.key}`}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${view === v.key ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>

          {view === "month" && (
            <MonthView cursor={cursor} range={range} events={events} onDayClick={(d) => openCreate(d)} onEventClick={openEdit} />
          )}
          {view === "week" && (
            <WeekView cursor={cursor} events={events} onSlotClick={(d) => openCreate(d)} onEventClick={openEdit} />
          )}
          {view === "day" && (
            <DayView cursor={cursor} events={events} onSlotClick={(d) => openCreate(d)} onEventClick={openEdit} />
          )}

          <div className="mt-6 flex items-center justify-between gap-2 text-xs text-muted-foreground">
            <div>Collegato come <span className="font-mono">{status.google_email || "—"}</span></div>
            <Button variant="ghost" size="sm" onClick={disconnect} className="rounded-full gap-1 text-destructive hover:text-destructive" data-testid="gcal-disconnect-btn">
              <LinkSimpleBreak size={14} /> Scollega
            </Button>
          </div>
        </>
      )}

      <EventDialog
        open={dialog.open}
        onOpenChange={(o) => setDialog({ ...dialog, open: o })}
        editing={dialog.editing}
        defaultDay={selectedDay}
        onSaved={() => { setDialog({ open: false, editing: null }); loadEvents(); }}
        onDeleted={() => { setDialog({ open: false, editing: null }); loadEvents(); }}
        onDelete={removeEvent}
      />
    </div>
  );
}

// ---------- Month view ----------
function MonthView({ cursor, range, events, onDayClick, onEventClick }) {
  const days = [];
  let d = range.gridStart;
  while (d <= range.gridEnd) {
    days.push(d);
    d = addDays(d, 1);
  }
  const weekdays = ["Lun","Mar","Mer","Gio","Ven","Sab","Dom"];
  const today = new Date();

  return (
    <Card className="border-border/70 rounded-lg overflow-hidden">
      <div className="grid grid-cols-7 border-b border-border/60 bg-secondary/40">
        {weekdays.map((w) => (
          <div key={w} className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{w}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 auto-rows-[minmax(110px,auto)]">
        {days.map((day) => {
          const inMonth = isSameMonth(day, cursor);
          const isToday = isSameDay(day, today);
          const dayEvents = events.filter((ev) => eventStartsInInterval(ev, startOfDay(day), endOfDay(day)));
          return (
            <div
              key={day.toISOString()}
              onClick={() => onDayClick(day)}
              data-testid={`day-cell-${format(day, "yyyy-MM-dd")}`}
              className={`border-r border-b border-border/50 p-1.5 cursor-pointer hover:bg-secondary/30 transition-colors ${inMonth ? "" : "bg-secondary/20 text-muted-foreground"}`}
            >
              <div className={`text-xs mb-1 inline-flex items-center justify-center h-6 w-6 rounded-full ${isToday ? "bg-primary text-primary-foreground font-bold" : ""}`}>
                {format(day, "d")}
              </div>
              <div className="space-y-1">
                {dayEvents.slice(0, 3).map((ev) => (
                  <button
                    key={ev.id}
                    onClick={(e) => { e.stopPropagation(); onEventClick(ev); }}
                    className="w-full text-left text-xs px-1.5 py-1 rounded bg-primary/15 text-primary hover:bg-primary/25 truncate block"
                    data-testid={`event-chip-${ev.id}`}
                  >
                    {!ev.all_day && (
                      <span className="font-mono mr-1">{format(parseISO(ev.start), "HH:mm")}</span>
                    )}
                    {ev.summary}
                  </button>
                ))}
                {dayEvents.length > 3 && (
                  <div className="text-[10px] text-muted-foreground px-1.5">+ {dayEvents.length - 3} altri</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ---------- Week view (agenda-style, hourly) ----------
function WeekView({ cursor, events, onSlotClick, onEventClick }) {
  const start = startOfWeek(cursor, { weekStartsOn: 1 });
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  return (
    <Card className="border-border/70 rounded-lg overflow-hidden">
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const dayEvents = events
            .filter((ev) => eventStartsInInterval(ev, startOfDay(day), endOfDay(day)))
            .sort((a, b) => new Date(a.start) - new Date(b.start));
          const isToday = isSameDay(day, new Date());
          return (
            <div key={day.toISOString()} className="border-r border-border/50 last:border-r-0 min-h-[400px]">
              <div className={`px-3 py-2 border-b border-border/50 ${isToday ? "bg-primary/10" : "bg-secondary/40"}`}>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{format(day, "EEE", { locale: it })}</div>
                <div className={`text-lg font-bold ${isToday ? "text-primary" : ""}`}>{format(day, "d")}</div>
              </div>
              <div className="p-2 space-y-1.5 cursor-pointer hover:bg-secondary/20 min-h-[300px]" onClick={() => onSlotClick(day)}>
                {dayEvents.length === 0 && <div className="text-[11px] text-muted-foreground/50 text-center pt-6">—</div>}
                {dayEvents.map((ev) => (
                  <button
                    key={ev.id}
                    onClick={(e) => { e.stopPropagation(); onEventClick(ev); }}
                    className="w-full text-left rounded-md px-2 py-1.5 bg-primary/10 hover:bg-primary/20 border-l-2 border-primary"
                    data-testid={`week-event-${ev.id}`}
                  >
                    <div className="font-mono text-[10px] text-primary/80">
                      {ev.all_day ? "TUTTO IL GIORNO" : `${format(parseISO(ev.start), "HH:mm")} – ${format(parseISO(ev.end), "HH:mm")}`}
                    </div>
                    <div className="text-xs font-medium truncate">{ev.summary}</div>
                    {ev.location && <div className="text-[10px] text-muted-foreground truncate">{ev.location}</div>}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ---------- Day view (hourly grid) ----------
function DayView({ cursor, events, onSlotClick, onEventClick }) {
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const dayStart = startOfDay(cursor);
  const dayEvents = events
    .filter((ev) => eventStartsInInterval(ev, startOfDay(cursor), endOfDay(cursor)))
    .sort((a, b) => new Date(a.start) - new Date(b.start));

  return (
    <Card className="border-border/70 rounded-lg overflow-hidden">
      <div className="grid grid-cols-[80px_1fr]">
        {hours.map((h) => {
          const slotTime = addHours(dayStart, h);
          const slotEvents = dayEvents.filter((ev) => {
            if (ev.all_day) return h === 0;
            const s = parseISO(ev.start);
            return s.getHours() === h;
          });
          return (
            <div key={h} className="contents">
              <div className="border-r border-b border-border/50 px-2 py-2 text-xs font-mono text-muted-foreground text-right">
                {String(h).padStart(2, "0")}:00
              </div>
              <div
                className="border-b border-border/50 p-1.5 min-h-[52px] cursor-pointer hover:bg-secondary/20"
                onClick={() => onSlotClick(slotTime)}
              >
                {slotEvents.map((ev) => (
                  <button
                    key={ev.id}
                    onClick={(e) => { e.stopPropagation(); onEventClick(ev); }}
                    className="w-full text-left rounded px-2 py-1 bg-primary/15 hover:bg-primary/25 border-l-2 border-primary mb-1"
                    data-testid={`day-event-${ev.id}`}
                  >
                    <div className="text-xs font-medium">{ev.summary}</div>
                    <div className="text-[10px] text-muted-foreground font-mono">
                      {ev.all_day ? "Tutto il giorno" : `${format(parseISO(ev.start), "HH:mm")} – ${format(parseISO(ev.end), "HH:mm")}`}
                      {ev.location ? ` · ${ev.location}` : ""}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ---------- Event Dialog (create/edit) ----------
function EventDialog({ open, onOpenChange, editing, defaultDay, onSaved, onDelete }) {
  const [form, setForm] = useState({ summary: "", description: "", location: "", start: "", end: "", all_day: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        summary: editing.summary || "",
        description: editing.description || "",
        location: editing.location || "",
        start: editing.all_day ? (editing.start || "").slice(0, 10) : localInputValue(editing.start),
        end: editing.all_day ? (editing.end || "").slice(0, 10) : localInputValue(editing.end),
        all_day: !!editing.all_day,
      });
    } else {
      const base = defaultDay || new Date();
      const start = new Date(base);
      start.setHours(9, 0, 0, 0);
      const end = new Date(start);
      end.setHours(10, 0, 0, 0);
      setForm({
        summary: "",
        description: "",
        location: "",
        start: localInputValue(start.toISOString()),
        end: localInputValue(end.toISOString()),
        all_day: false,
      });
    }
  }, [open, editing, defaultDay]);

  const save = async () => {
    if (!form.summary.trim()) return toast.error("Titolo richiesto");
    if (!form.start || !form.end) return toast.error("Date/orari richiesti");
    setSaving(true);
    try {
      const payload = {
        summary: form.summary,
        description: form.description || null,
        location: form.location || null,
        all_day: form.all_day,
        start: form.all_day ? form.start : inputToIso(form.start),
        end: form.all_day ? form.end : inputToIso(form.end),
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Rome",
      };
      if (editing) {
        await api.patch(`/calendar/events/${editing.id}`, payload);
        toast.success("Evento aggiornato");
      } else {
        await api.post("/calendar/events", payload);
        toast.success("Evento creato");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="event-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarBlank size={20} weight="duotone" className="text-primary" />
            {editing ? "Modifica appuntamento" : "Nuovo appuntamento"}
          </DialogTitle>
          <DialogDescription>
            {editing ? "Le modifiche verranno salvate su Google Calendar." : "Verrà creato direttamente sul tuo Google Calendar."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest">Titolo</Label>
            <Input value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} data-testid="event-title-input" className="mt-1.5" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.all_day} onChange={(e) => setForm({ ...form, all_day: e.target.checked })} data-testid="event-allday-checkbox" />
            Tutto il giorno
          </label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest">Inizio</Label>
              <Input
                type={form.all_day ? "date" : "datetime-local"}
                value={form.start}
                onChange={(e) => setForm({ ...form, start: e.target.value })}
                data-testid="event-start-input"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest">Fine</Label>
              <Input
                type={form.all_day ? "date" : "datetime-local"}
                value={form.end}
                onChange={(e) => setForm({ ...form, end: e.target.value })}
                data-testid="event-end-input"
                className="mt-1.5"
              />
            </div>
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest">Luogo</Label>
            <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Es. Via Roma 12, oppure vuoto" data-testid="event-location-input" className="mt-1.5" />
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest">Note</Label>
            <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="event-description-input" className="mt-1.5" />
          </div>
        </div>

        <DialogFooter className="gap-2">
          {editing && (
            <Button variant="ghost" className="text-destructive hover:text-destructive gap-1 mr-auto" onClick={() => onDelete(editing)} data-testid="event-delete-btn">
              <Trash size={14} /> Elimina
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={save} disabled={saving} className="rounded-full gap-1" data-testid="event-save-btn">
            {editing ? <PencilSimple size={14} /> : <Plus size={14} />}
            {saving ? "Salvo…" : (editing ? "Aggiorna" : "Crea appuntamento")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
